import { useState, useMemo, useEffect } from "react";
import { Group } from "../../../../application/domain/group/group";
import {
  validateGroupsRolesSelected,
  validateRoleGroupConsistency,
  validateRoles,
  validateUserName,
} from "../../../../application/domain/user/user";
import { UserEntity } from "../../../../application/domain/user/user-entity";
import { validateEmail } from "../../../../utils/email-utils";

export type UserError = {
  name: string | undefined;
  email: string | undefined;
  roles: string | undefined;
  groupsAndRoles: string | undefined;
};

export const useUserBasicInfo = (args: {
  user: UserEntity;
  groups: Group[];
  fetchGroupCandidates: (name: string) => Promise<Group[]>;
}) => {
  const [inputUserInfo, setInputUserInfo] = useState(args.user);
  const [isEdit, setIsEdit] = useState(false);

  const shouldSelectGroup = useMemo(
    () => inputUserInfo?.roles.includes("Staff") || false,
    [inputUserInfo?.roles],
  );

  const nameError = useMemo(
    () => validateUserName(inputUserInfo?.name || ""),
    [inputUserInfo?.name],
  );

  const emailError = useMemo(
    () =>
      inputUserInfo?.email ? validateEmail(inputUserInfo.email) : undefined,
    [inputUserInfo?.email],
  );

  const rolesError = useMemo(
    () =>
      inputUserInfo?.roles ? validateRoles(inputUserInfo.roles) : undefined,
    [inputUserInfo?.roles],
  );

  const groupsError = useMemo(
    () =>
      inputUserInfo?.groupsRoles
        ? (validateGroupsRolesSelected(inputUserInfo.groupsRoles) ??
          validateRoleGroupConsistency(
            inputUserInfo.roles,
            inputUserInfo.groupsRoles,
          ))
        : undefined,
    [inputUserInfo],
  );

  const validationError = useMemo<UserError>(
    () => ({
      name: nameError,
      email: emailError,
      roles: rolesError,
      groupsAndRoles: groupsError,
    }),
    [nameError, emailError, rolesError, groupsError],
  );

  const isValidationError = useMemo(
    () =>
      Object.values(validationError).filter((value) => value !== undefined)
        .length > 0,
    [validationError],
  );

  useEffect(() => {
    setInputUserInfo(UserEntity.fromJSON(args.user));
  }, [args.user]);

  return {
    inputUserInfo,
    setInputUserInfo,
    isEdit,
    setIsEdit,
    shouldSelectGroup,
    validationError,
    isValidationError,
  };
};
